import { useRef, useCallback, useState } from 'react';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import LabelObjectRenderer from './LabelObjectRenderer';
import { TOOLBOX_DND_MIME, type ToolboxObjectType } from './Toolbox';
import type { LabelObject } from '@/types/labelTemplate';

// Plausible placeholder values for every field in client/src/lib/labelFields.ts — this is the
// designer's own live preview, independent of any real GRN/Material/Supplier (that's
// GrnLabelPrintPage.tsx's sampleData), so every bindable field needs SOME representative value
// here or its preview would render blank even though the field genuinely resolves at print time.
// Exported so LabelTemplateList.tsx's read-only card thumbnail can render the exact same preview
// look without duplicating this list.
export const SAMPLE_DATA = {
  MaterialCode: 'SKU-78-ALPHA',
  MaterialName: 'Precision Bearing X1',
  CategoryName: 'Bearings',
  UomCode: 'PCS',
  Barcode: '8901234567890',
  HsnCode: '84821010',
  GstRate: '18',
  ManufacturerName: 'Acme Precision Ltd.',
  AbcClass: 'A',
  SupplierCode: 'SUP-0042',
  SupplierName: 'Sunrise Industrial Supplies',
  SupplierGstin: '27ABCDE1234F1Z5',
  SupplierAddress: 'Plot 14, MIDC Industrial Area, Pune',
  SupplierContactPerson: 'Ramesh Iyer',
  SupplierPhone: '+91 98765 43210',
  CustomerCode: 'CUST-0017',
  CustomerName: 'Northline Retail Pvt. Ltd.',
  CustomerGstin: '29XYZAB5678C1Z9',
  CustomerAddress: '221 Commerce Street, Bengaluru',
  CustomerContactPerson: 'Priya Nair',
  CustomerPhone: '+91 91234 56789',
  GrnNumber: 'GRN-2026-00042',
  PurchaseOrderNumber: 'PO-2026-00118',
  WarehouseName: 'Main Warehouse',
  GrnDate: dayjs().format('MMM D, YYYY'),
  VehicleNo: 'MH12AB1234',
  InwardDocNo: 'INW-00981',
  ClientName: 'Meridian Retail Group',
  BatchNo: 'B-2026-014',
  MfgDate: dayjs().subtract(30, 'day').format('MMM D, YYYY'),
  ExpiryDate: dayjs().add(11, 'month').format('MMM D, YYYY'),
  OrderedQty: '500',
  ReceivedQty: '500',
  AcceptedQty: '480',
  RejectedQty: '20',
  UnitPrice: '124.50',
  Qty: '10',
  PackingTaskNumber: 'PKG-2026-00042',
  PickListNumber: 'PL-2026-00031',
  OrderNumber: 'DO-2026-00058',
  SourceNumber: 'DO-2026-00058',
  Status: 'Completed',
  PackageNo: 'PKG-2026-00042-1',
  Weight: '12.5',
  Remarks: 'Handle with care',
  CarrierName: 'BlueDart',
  RequestedQty: '25',
  IssuedQty: '25',
  PrintDate: dayjs().format('MMM D, YYYY'),
  PrintTime: dayjs().format('HH:mm'),
};

// mm-to-px scale for on-screen rendering at 100% zoom
export const CANVAS_SCALE = 3.5;

// Below this, a corner drag has squashed the object to the point it'd be fiddly to grab again.
const MIN_SIZE_MM = 5;

type Corner = 'nw' | 'ne' | 'sw' | 'se';
const CORNERS: { corner: Corner; cursor: string }[] = [
  { corner: 'nw', cursor: 'nwse-resize' },
  { corner: 'ne', cursor: 'nesw-resize' },
  { corner: 'sw', cursor: 'nesw-resize' },
  { corner: 'se', cursor: 'nwse-resize' },
];

interface ResizeHandlesProps {
  object: LabelObject;
  scale: number;
  onResizeStart: (object: LabelObject, corner: Corner, e: React.MouseEvent) => void;
}

/** Four small squares at the selected object's corners — drag one to resize from that corner while the opposite corner stays anchored in place. */
function ResizeHandles({ object, scale, onResizeStart }: ResizeHandlesProps) {
  return (
    <>
      {CORNERS.map(({ corner, cursor }) => {
        const left = (corner === 'ne' || corner === 'se' ? object.x + object.w : object.x) * scale;
        const top = (corner === 'sw' || corner === 'se' ? object.y + object.h : object.y) * scale;
        return (
          <Box
            key={corner}
            onMouseDown={(e) => onResizeStart(object, corner, e)}
            sx={{
              position: 'absolute',
              left: left - 5,
              top: top - 5,
              width: 10,
              height: 10,
              bgcolor: 'primary.main',
              border: '2px solid #ffffff',
              borderRadius: '2px',
              boxShadow: '0 0 2px rgba(0,0,0,0.4)',
              cursor,
              zIndex: 10,
            }}
          />
        );
      })}
    </>
  );
}

interface LabelCanvasProps {
  objects: LabelObject[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onMove: (id: string, dx: number, dy: number) => void;
  onResize: (id: string, patch: Partial<LabelObject>) => void;
  onDropObject?: (type: ToolboxObjectType, xMm: number, yMm: number) => void;
  widthMm: number;
  heightMm: number;
  zoom?: number;
}

export default function LabelCanvas({ objects, selectedId, onSelect, onMove, onResize, onDropObject, widthMm, heightMm, zoom = 1 }: LabelCanvasProps) {
  const dragState = useRef<{ id: string; startX: number; startY: number } | null>(null);
  const labelSurfaceRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const scale = CANVAS_SCALE * zoom;

  const handleResizeStart = useCallback(
    (object: LabelObject, corner: Corner, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onSelect(object.id);
      const start = { x: object.x, y: object.y, w: object.w, h: object.h, mouseX: e.clientX, mouseY: e.clientY };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const dxMm = (moveEvent.clientX - start.mouseX) / scale;
        const dyMm = (moveEvent.clientY - start.mouseY) / scale;

        let { x, y, w, h } = start;
        if (corner === 'se' || corner === 'ne') w = Math.max(MIN_SIZE_MM, start.w + dxMm);
        else {
          w = Math.max(MIN_SIZE_MM, start.w - dxMm);
          x = start.x + (start.w - w);
        }
        if (corner === 'se' || corner === 'sw') h = Math.max(MIN_SIZE_MM, start.h + dyMm);
        else {
          h = Math.max(MIN_SIZE_MM, start.h - dyMm);
          y = start.y + (start.h - h);
        }

        onResize(object.id, { x, y, w, h });
      };
      const handleMouseUp = () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [onSelect, onResize, scale],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(TOOLBOX_DND_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOver(true);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      const type = e.dataTransfer.getData(TOOLBOX_DND_MIME) as ToolboxObjectType;
      setDragOver(false);
      if (!type || !labelSurfaceRef.current || !onDropObject) return;
      e.preventDefault();
      // The drop point becomes the new object's top-left corner (handleDropObject re-centers it
      // under the cursor once it knows that object type's default width/height).
      const rect = labelSurfaceRef.current.getBoundingClientRect();
      const xMm = (e.clientX - rect.left) / scale;
      const yMm = (e.clientY - rect.top) / scale;
      onDropObject(type, xMm, yMm);
    },
    [onDropObject, scale],
  );

  const handlePointerDown = useCallback(
    (id: string, e: React.MouseEvent) => {
      onSelect(id);
      dragState.current = { id, startX: e.clientX, startY: e.clientY };

      const handleMove = (moveEvent: MouseEvent) => {
        if (!dragState.current) return;
        const dx = (moveEvent.clientX - dragState.current.startX) / scale;
        const dy = (moveEvent.clientY - dragState.current.startY) / scale;
        onMove(dragState.current.id, dx, dy);
        dragState.current.startX = moveEvent.clientX;
        dragState.current.startY = moveEvent.clientY;
      };
      const handleUp = () => {
        dragState.current = null;
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
      };
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
    },
    [onMove, onSelect, scale],
  );

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        bgcolor: '#e8eaed',
        backgroundImage:
          'linear-gradient(45deg, #dde0e3 25%, transparent 25%), linear-gradient(-45deg, #dde0e3 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #dde0e3 75%), linear-gradient(-45deg, transparent 75%, #dde0e3 75%)',
        backgroundSize: '16px 16px',
        backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
      }}
      onMouseDown={() => onSelect(null)}
      onDragOver={handleDragOver}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <Box
        ref={labelSurfaceRef}
        sx={{
          position: 'relative',
          width: widthMm * scale,
          height: heightMm * scale,
          bgcolor: '#ffffff',
          boxShadow: '0px 8px 24px rgba(0,0,0,0.15)',
          borderRadius: 1,
          outline: (theme) => `2px ${dragOver ? 'dashed' : 'solid'} ${dragOver ? theme.palette.primary.main : 'transparent'}`,
          outlineOffset: 2,
          transition: 'outline-color 0.15s ease',
        }}
      >
        {objects.map((obj) => (
          <LabelObjectRenderer key={obj.id} object={obj} sampleData={SAMPLE_DATA} selected={obj.id === selectedId} onSelect={(id, e) => handlePointerDown(id, e)} scale={scale} />
        ))}
        {(() => {
          const selectedObject = objects.find((o) => o.id === selectedId);
          return selectedObject ? <ResizeHandles object={selectedObject} scale={scale} onResizeStart={handleResizeStart} /> : null;
        })()}
      </Box>
    </Box>
  );
}
